<script setup lang="ts">
import { onMounted, ref } from 'vue'
import Toast from '@/components/shared/Toast.vue'
import { pullAppConfig } from '@/config'
import { useAuthStore } from '@/store/auth'
import { useEnvironment } from '@/composables/useEnvironment'

const appReady = ref(false)
const initError = ref<string | null>(null)

// The selected environment (main / sections / langschool) picks which API host
// every view queries. Views read it once at setup and bake it into their query
// keys, so switching environments must remount the routed view — otherwise the
// list keeps showing the previous environment's data and never re-fetches.
const { currentServiceKey } = useEnvironment()

onMounted(async () => {
  try {
    const result = await pullAppConfig()
    if (result.errors) {
      initError.value = result.errors.map((e) => e.message).join('; ')
      return
    }

    const auth = useAuthStore()
    await auth.initialize()
    appReady.value = true
  } catch (e) {
    initError.value = e instanceof Error ? e.message : 'Failed to initialize app'
  }
})
</script>

<template>
  <div v-if="initError" class="app-error">
    <div class="app-error__card">
      <h1 class="app-error__title">Initialization Error</h1>
      <p class="app-error__message">{{ initError }}</p>
    </div>
  </div>

  <template v-else-if="appReady">
    <router-view :key="currentServiceKey" />
    <Toast />
  </template>

  <div v-else class="app-loading">
    <p>Loading...</p>
  </div>
</template>

<style scoped>
.app-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
}

.app-error__card {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  max-width: 480px;
  width: 100%;
  text-align: center;
}

.app-error__title {
  font-size: 20px;
  font-weight: 600;
  color: #dc2626;
  margin: 0 0 8px;
}

.app-error__message {
  font-size: 14px;
  color: #57534e;
  margin: 0;
}

.app-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: #78716c;
  font-size: 16px;
}
</style>
